import styled, { css } from 'styled-components';
import { NegativeFill } from 'ui/icons/status/NegativeFill';
import { body2, subtleShadow, colors, duration, easing, spacing, backgroundColors } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { VARIANTS } from './constants';
import { ErrorTheme } from './types';

const inputBaseStyles = {
  background: colors.white,
  borderColor: colors.grey15,
  boxShadow: 'none',
  color: colors.textPrimary,
  cursor: 'text',
  placeholderColor: colors.textSecondary,
  prefixSvgColor: colors.grey50,
};

const inputErrorStyles = {
  borderColor: colors.red100,
};

const inputHoverStyles = {
  borderColor: colors.purple50,
};

const inputFocusStyles = {
  borderColor: inputHoverStyles.borderColor,
  boxShadow: `0px 0px 0px 1px ${inputHoverStyles.borderColor}`,
  prefixSvgColor: colors.purple100,
};

const inputReadOnlyStyles = {
  ...inputBaseStyles,
  background: colors.grey1,
  boxShadow: `0px 0px 1px rgba(0,0,0,0.08), 0px 0px 2px rgba(0,0,0,0.06), 0px 0px 1px rgba(0,0,0,0.06)`,
  color: colors.textSecondary,
  cursor: 'not-allowed',
};

const inputStyles = {
  ...inputBaseStyles,
  [VARIANTS.FOCUS]: inputFocusStyles,
  [VARIANTS.HOVER]: inputHoverStyles,
  [VARIANTS.HAS_ERROR]: inputErrorStyles,
  [VARIANTS.READ_ONLY]: inputReadOnlyStyles,
};

export const StyledInput = styled.input`
  ${body2};
  box-shadow: ${inputStyles.boxShadow}; /** overrides global CSS on type="text" */
  box-sizing: border-box;
  border: none;
  display: block;
  flex: 1 1 auto;
  outline: none;
  cursor: ${inputStyles.cursor};

  :active {
    border: none;
  }

  ::placeholder {
    color: ${inputStyles.placeholderColor};
  }

  &[type='number'] {
    /* Disables the up/down arrows in firefox as it was causing sizing issues
    Numbers can still be incremented with keyboard up-arrow/down-arrow */
    -moz-appearance: textfield;
  }
`;

interface InputErrorProps {
  errorTheme?: ErrorTheme;
}

const getErrorColor = ({ errorTheme }: InputErrorProps) =>
  errorTheme?.errorColor || inputErrorStyles.borderColor;

/**
 * Returns styles for the error state
 */
export const createInputErrorCSS = (props: InputErrorProps) => {
  const errorColor = getErrorColor(props);

  return css`
    border-color: ${errorColor};

    :hover {
      border-color: ${errorColor};
    }
  `;
};

/**
 * Returns styles for the focused state
 */
export const createInputFocusCSS = ({
  boxShadow = inputFocusStyles.boxShadow,
  borderColor = inputFocusStyles.borderColor,
  prefixSvgColor = inputFocusStyles.prefixSvgColor,
}: typeof inputFocusStyles) => css`
  :focus-within, &[data-show-focused='true'] {
    box-shadow: ${boxShadow};
    border-color: ${borderColor};

    & .input-prefix svg {
      color: ${prefixSvgColor};
    }
  }
`;

/**
 * Returns styles for the read-only state
 */
export const createInputReadonlyCSS = () => {
  const commonStyles = css`
    background: ${inputReadOnlyStyles.background};
    color: ${inputReadOnlyStyles.color};
    cursor: ${inputReadOnlyStyles.cursor};
  `;

  return css`
    ${commonStyles}

    ${StyledInput} {
      ${commonStyles}
    }

    :hover {
      border-color: ${inputReadOnlyStyles.borderColor};
    }

    ${createInputFocusCSS(inputReadOnlyStyles)}
  `;
};

/**
 * NOTE: Readonly and disabled should have different styles as their utility is different.
 * However, for now, we are using the same styles for both -- especially since
 * we are not consistent in where we use `disabled` vs `readonly` in our codebase.
 */
export const createInputDisabledCSS = createInputReadonlyCSS;

interface StyledWrapperProps extends InputErrorProps {
  hasError?: boolean;
  isReadOnly?: boolean;
  width?: string;
  $hasWrapperStyling?: boolean;
}

export const StyledWrapper = styled.div<StyledWrapperProps>`
  ${body2}
  align-items: center;
  border-width: ${spacing.single};
  border-style: solid;
  border-color: ${inputStyles.borderColor};
  border-radius: ${spacing.quarter};
  padding: ${spacing.half} ${spacing.threeQuarter};
  display: flex;
  justify-content: space-between;
  position: relative;
  background: ${inputStyles.background};
  box-shadow: ${subtleShadow.boxShadow};
  transition: all ${duration.short} ${easing.exit};

  & .input-prefix svg {
    color: ${inputStyles.prefixSvgColor};
  }

  :hover {
    border-color: ${inputHoverStyles.borderColor};
    transition: all ${duration.short} ${easing.entrance};
  }

  ${createInputFocusCSS(inputFocusStyles)}
  ${({ isReadOnly }) => isReadOnly && createInputReadonlyCSS()};
  ${(props) => props.hasError && createInputErrorCSS(props)}
  ${({ width }) => width && `width: ${width}`};

  > * + * {
    margin-left: ${spacing.half};
  }

  /**
    Sticky styles are used only in the FilterSelect, where the input must remain
    at the top of the option list.
  */
  &[data-sticky='true'] {
    position: sticky;
    top: 0;
    z-index: 2;

    /**
      This box-shadow adds white background around the InputText so that when the
      user scrolls, the options aren't seen behind the input.
    */
    box-shadow: 0 0 0 ${spacing.half} ${backgroundColors.backgroundPrimary};
    margin-bottom: ${spacing.base};
    &:after {
      /**
        This adds the bottom border at the bottom of the sticky wrapper.
      */
      content: ' ';
      border: ${spacing.single} solid ${colors.borderPrimarySubtle};
      bottom: -${pxToRem(9)};
      left: -${spacing.half};
      position: absolute;
      right: -${spacing.half};
    }
  }
`;

export const Positioner = styled.div`
  align-items: center;
  color: ${(props) => props.color};
  display: flex;
  flex: 0 0 auto;

  &:has([data-ai-control="true"]) {
    justify-content: flex-end;
    margin: 0;
    padding: 0 ${pxToRem(1)} ${pxToRem(1)} 0;
    width: 100%;
  }
`;

export const StyledNegativeFill = styled(NegativeFill)<InputErrorProps>`
  stroke: ${colors.white};
  path {
    fill: ${({ color, ...props }) => color || getErrorColor(props)};
  }
`;
